import type { Schemas } from './generated/theboatdb-typed-openapi';

/**
 * The one error type this client throws.
 *
 * TheBoatDB API answers every failure with the same envelope — `{ error: { code,
 * message, requestId } }`, where `code` comes from a closed set the service
 * treats as part of its contract (`apps/theboatdb_api/src/lib/error-codes.ts`).
 * Callers branch on that `code`, never on the status or the message: a free user
 * gets an upgrade prompt, a rate-limited user gets a wait, and both of those are
 * one copy edit away from breaking if a screen matches on prose instead.
 *
 * Mapping happens once, in the fetcher, so no hook or component has to know the
 * envelope exists.
 */

/** The closed set, taken from the generated schema so it cannot drift from the service's. */
export type TheBoatDbErrorCode = Schemas.Error['error']['code'];

export const THEBOATDB_ERROR_CODES = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UNAUTHORIZED: 'UNAUTHORIZED',
  MISSING_API_KEY: 'MISSING_API_KEY',
  INVALID_API_KEY: 'INVALID_API_KEY',
  FORBIDDEN: 'FORBIDDEN',
  INSUFFICIENT_PERMISSIONS: 'INSUFFICIENT_PERMISSIONS',
  PRO_SUBSCRIPTION_REQUIRED: 'PRO_SUBSCRIPTION_REQUIRED',
  INVALID_SIGNATURE: 'INVALID_SIGNATURE',
  NOT_FOUND: 'NOT_FOUND',
  METHOD_NOT_ALLOWED: 'METHOD_NOT_ALLOWED',
  KEY_LIMIT_REACHED: 'KEY_LIMIT_REACHED',
  PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
  QUOTA_EXCEEDED: 'QUOTA_EXCEEDED',
  RATE_LIMITED: 'RATE_LIMITED',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
} as const satisfies Record<TheBoatDbErrorCode, TheBoatDbErrorCode>;

/**
 * Codes for which retrying the same request is pointless.
 *
 * A lapsed subscription, a rejected session and a key that is already gone are
 * settled answers, not blips — retrying a free user's request three times only
 * delays their upgrade prompt. `RATE_LIMITED` is here too: the limiter is what a
 * retry would be arguing with.
 */
const TERMINAL_CODES: readonly TheBoatDbErrorCode[] = [
  THEBOATDB_ERROR_CODES.UNAUTHORIZED,
  THEBOATDB_ERROR_CODES.FORBIDDEN,
  THEBOATDB_ERROR_CODES.PRO_SUBSCRIPTION_REQUIRED,
  THEBOATDB_ERROR_CODES.VALIDATION_ERROR,
  THEBOATDB_ERROR_CODES.NOT_FOUND,
  THEBOATDB_ERROR_CODES.METHOD_NOT_ALLOWED,
  THEBOATDB_ERROR_CODES.KEY_LIMIT_REACHED,
  THEBOATDB_ERROR_CODES.PAYLOAD_TOO_LARGE,
  THEBOATDB_ERROR_CODES.RATE_LIMITED,
];

export class TheBoatDbApiError extends Error {
  readonly code: TheBoatDbErrorCode;
  readonly status: number;
  /** The API's request id, worth quoting in an error state so support can find the log line. */
  readonly requestId: string | null;
  /** Seconds to wait, parsed from `Retry-After`. `null` when the header is absent. */
  readonly retryAfterSeconds: number | null;
  /** Present on a validation failure, naming the offending field. */
  readonly details: unknown;

  constructor(params: {
    code: TheBoatDbErrorCode;
    message: string;
    status: number;
    requestId?: string | null;
    retryAfterSeconds?: number | null;
    details?: unknown;
  }) {
    super(params.message);
    this.name = 'TheBoatDbApiError';
    this.code = params.code;
    this.status = params.status;
    this.requestId = params.requestId ?? null;
    this.retryAfterSeconds = params.retryAfterSeconds ?? null;
    this.details = params.details;
  }

  /** Whether retrying this exact request could plausibly succeed. */
  get isRetryable(): boolean {
    return !TERMINAL_CODES.includes(this.code);
  }
}

export function isTheBoatDbApiError(error: unknown): error is TheBoatDbApiError {
  return error instanceof TheBoatDbApiError;
}

/** Narrow helper components read, so no component has to know the error class. */
export function hasErrorCode(error: unknown, code: TheBoatDbErrorCode): boolean {
  return isTheBoatDbApiError(error) && error.code === code;
}

const KNOWN_CODES = new Set<string>(Object.values(THEBOATDB_ERROR_CODES));

function parseRetryAfter(response: Response): number | null {
  const header = response.headers.get('Retry-After');

  if (header === null) {
    return null;
  }

  const seconds = Number.parseInt(header, 10);

  return Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

/**
 * A failure response is still a network response, so its body can be anything —
 * an envelope from the service, an HTML error page from a proxy in front of it,
 * or nothing at all. An unreadable body maps to a generic code rather than
 * throwing a parse error over the top of the real failure.
 */
export async function toTheBoatDbApiError(response: Response): Promise<TheBoatDbApiError> {
  let envelope: Partial<Schemas.Error> = {};

  try {
    envelope = (await response.json()) as Partial<Schemas.Error>;
  } catch {
    envelope = {};
  }

  const rawCode = envelope.error?.code;
  const code =
    typeof rawCode === 'string' && KNOWN_CODES.has(rawCode)
      ? (rawCode as TheBoatDbErrorCode)
      : THEBOATDB_ERROR_CODES.INTERNAL_ERROR;

  return new TheBoatDbApiError({
    code,
    message: envelope.error?.message ?? response.statusText ?? 'The request failed.',
    status: response.status,
    requestId: envelope.error?.requestId ?? null,
    retryAfterSeconds: parseRetryAfter(response),
    details: envelope.error?.details,
  });
}
